import { htmlEscape } from '@mimir/common/util/escape';
import type { FieldError, MetadataErrors } from '@mimir/common/dto/mdfModels';
import { exceptionSummary, exceptionToString } from '@mimir/common/util/exception';
import { showToast, DONT_AUTO_HIDE_TOAST, TOAST } from '@mimir/web/eventBus';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';

export function showUiErrorMessage(msg: string) {
  showToast(msg, TOAST.ERROR);
}

/**
 * @param message Message shown to the user in error toast
 * @param err The caught error object
 * @param devMessage Optional dev message that will be written to the console but not shown to the user
 */
export function logErrorAndShowToast(message: string, err: any, devMessage?: string, requireExplicitClose?: boolean) {
  if (devMessage) {
    console.log(devMessage);
  }
  console.error(message, ':', exceptionToString(err));
  const stackCapture = new Error();
  (window as any).last_error = err;
  (window as any).last_reporter_stack = stackCapture.stack;
  showToast(message + ': ' + exceptionSummary(err), TOAST.ERROR, requireExplicitClose === true ? DONT_AUTO_HIDE_TOAST : undefined);
}


export function logErrorForInlineDisplay(context: string, err: unknown): string {
  console.error(context, ':', exceptionToString(err));
  return exceptionSummary(err);
}

export const getFieldErrorTooltipHtml = (errors: Record<string, FieldError>, errorOriginatedIn?: string) => {
  let errorTooltip = '<small>';
  errorTooltip += `<div>Please correct errors detected in ${errorOriginatedIn ?? ''} :</div>`;
  errorTooltip += getFieldErrorListHtml(errors);
  errorTooltip += '</small>';
  return errorTooltip;
};

/**
 * Can be used as an alternative to the template above, in the cases where using MdfErrorTooltip component is cumbersome.
 */
export const getMdfErrorTooltipHtml = (errors: MetadataErrors, type?: string) => {
  const errorOriginatedIn = type ? `${type} metadata` : 'metadata';
  return getFieldErrorTooltipHtml(errors, errorOriginatedIn);
};

export const getFieldErrorListHtml = (errors: Record<string, Pick<FieldError, 'label' | 'errors'>>) => {
  let listErrorsTooltip = '';
  for (const field of Object.keys(errors)) {
    listErrorsTooltip += '<div>';
    listErrorsTooltip += `<div>Field: <b>${htmlEscape(errors[field].label)}</b></div>`;
    listErrorsTooltip += '<ol>';

    for (const err of errors[field].errors) {
      listErrorsTooltip += `<li>${htmlEscape(err)}</li>`;
    }
    listErrorsTooltip += '</ol></div>';
  }
  return listErrorsTooltip;
};

/**
 * Builds a SafeHtmlBuilder object containing formatted MDF error information
 * for use in tooltips and error displays.
 */
export function mdfErrorHtmlTooltip(errors: MetadataErrors): SafeHtmlBuilder {
  const builder = new SafeHtmlBuilder();

  builder.addDiv(b => {
    b.addText('Please correct errors detected in metadata:');
  });

  for (const field of Object.keys(errors)) {
    const error = errors[field];
    builder.addDiv(b => {
      b.addDiv(innerB => {
        innerB.addText('Field: ');
        innerB.addBoldText(error.label);
      });
      b.addOrderedList(error.errors);
    });
  }

  return builder;
}

export const inDevelopmentMode = () => Boolean((window as any).mimirEnv);
